import { NgClass } from '@angular/common';
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    forwardRef,
    input,
    model,
    output,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputSelectWrapperComponent } from '../input-select-wrapper/input-select-wrapper.component';
import { InputWrapperComponent } from '../input-wrapper/input-wrapper.component';

let inputId = 0;

@Component({
    selector: 'ui-input-autocomplete',
    standalone: true,
    imports: [InputWrapperComponent, InputSelectWrapperComponent, NgClass],
    templateUrl: './input-autocomplete.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => InputAutocompleteComponent), multi: true }],
})
export class InputAutocompleteComponent<T> implements ControlValueAccessor {
    value = model<string | null>(null);
    selectedValue = model<{ label: string; value: T }>();

    searchChange = output<string | null>();

    disabled = model(false);
    inputDisabled = signal(false);
    label = input('');
    /** Liste unter dem Feld statt als schwebendes Overlay (CDK). */
    noOverlay = input(false, { transform: booleanAttribute });
    error = input(false, { transform: booleanAttribute });
    withIcon = input(false, { transform: booleanAttribute });
    errorMsg = input<string>();

    focussed = signal(false);
    open = signal(false);

    inputId = `input-autocompleteId-${inputId++}`;

    autocompleteOptions = input<Array<{ label: string; value: T }>>();

    _onChange?: (value: T) => void;
    _onTouch?: () => void;

    writeValue(value: { label: string; value: T }): void {
        this.selectedValue.set(value);
    }
    registerOnChange(fn: (value: T) => void) {
        this._onChange = fn;
    }
    registerOnTouched(fn: () => void) {
        this._onTouch = fn;
    }

    isDisabled = computed(() => this.disabled() || this.inputDisabled());

    setDisabledState?(isDisabled: boolean) {
        this.inputDisabled.set(isDisabled);
    }

    onInput(event: Event) {
        const { value } = event.target as HTMLInputElement;
        this.searchChange.emit(value);
        this.value.set(value);
        if (!this.selectedValue()) {
            this.selectedValue.set(undefined);
        }
        this.open.set(true);
    }

    selectAutocomplete(option: { label: string; value: T }) {
        this.selectedValue.set(option);
        this._onChange?.(option.value);
        this.value.set(null);
        this.searchChange.emit(null);
        this.open.set(false);
    }

    onEnter() {
        const firstAutocompleteOption = this.autocompleteOptions()?.at(0);
        if (this.autocompleteOptions()?.length === 1 && firstAutocompleteOption) {
            this.selectAutocomplete(firstAutocompleteOption);
        }
    }

    onButtonClick() {
        const isOpen = this.open();
        this.open.set(!isOpen);

        if (!isOpen) {
            this.searchChange.emit(this.value() ?? '');
        }
    }
}
